
import React, { useState } from "react";
import { formatDistanceToNow } from "date-fns";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Checkbox } from "@/components/ui/checkbox";
import { Skeleton } from "@/components/ui/skeleton";
import { Package } from "lucide-react";
import OrderStatusBadge from "./OrderStatusBadge";
import PaymentStatusBadge from "./PaymentStatusBadge";
import OrderActionButtons from "./OrderActionButtons";
import BulkOrderActions from "./BulkOrderActions";
import { useOrderManagement } from "@/hooks/useOrderManagement";
import type { SafeOrderProfile } from "@/utils/typeGuards";

interface OrderItem {
  id: string;
  quantity: number;
  price: number;
  products: {
    name: string;
    image_url?: string;
  } | null;
}

interface Order {
  id: string;
  user_id: string | null;
  total_amount: number;
  status: string | null;
  payment_status: string | null;
  payment_method: string | null;
  created_at: string | null;
  updated_at: string | null;
  tracking_number?: string | null;
  profiles: SafeOrderProfile;
  order_items?: OrderItem[];
}

interface OrdersTableProps {
  orders: Order[];
  isLoading: boolean;
  onViewOrder: (order: Order) => void;
  onUpdateStatus: (orderId: string, status: string) => void;
}

const OrdersTable: React.FC<OrdersTableProps> = ({
  orders,
  isLoading,
  onViewOrder,
  onUpdateStatus,
}) => {
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const { bulkUpdateOrders } = useOrderManagement();

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedOrders(orders.map(order => order.id));
    } else {
      setSelectedOrders([]);
    }
  };

  const handleSelectOrder = (orderId: string, checked: boolean) => {
    if (checked) {
      setSelectedOrders(prev => [...prev, orderId]);
    } else {
      setSelectedOrders(prev => prev.filter(id => id !== orderId));
    }
  };

  const handleBulkAction = (action: string) => {
    bulkUpdateOrders({ orderIds: selectedOrders, status: action });
    setSelectedOrders([]);
  };

  const handleClearSelection = () => {
    setSelectedOrders([]);
  };

  if (isLoading) {
    return (
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground">Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center space-x-4">
                <Skeleton className="h-12 w-12 rounded bg-admin-muted" />
                <div className="space-y-2">
                  <Skeleton className="h-4 w-[200px] bg-admin-muted" />
                  <Skeleton className="h-4 w-[150px] bg-admin-muted" />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!orders || orders.length === 0) {
    return (
      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground">Orders</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="text-center py-8">
            <Package className="h-12 w-12 text-admin-muted-foreground mx-auto mb-3" />
            <p className="text-admin-muted-foreground">No orders found</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      <BulkOrderActions
        selectedOrders={selectedOrders}
        onBulkAction={handleBulkAction}
        onClearSelection={handleClearSelection}
      />

      <Card className="admin-card border-admin-border">
        <CardHeader>
          <CardTitle className="text-admin-foreground">
            Orders ({orders.length})
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-12">
                  <Checkbox
                    checked={selectedOrders.length === orders.length}
                    onCheckedChange={handleSelectAll}
                  />
                </TableHead>
                <TableHead className="text-admin-muted-foreground">Order ID</TableHead>
                <TableHead className="text-admin-muted-foreground">Customer</TableHead>
                <TableHead className="text-admin-muted-foreground">Items</TableHead>
                <TableHead className="text-admin-muted-foreground">Total</TableHead>
                <TableHead className="text-admin-muted-foreground">Status</TableHead>
                <TableHead className="text-admin-muted-foreground">Payment</TableHead>
                <TableHead className="text-admin-muted-foreground">Date</TableHead>
                <TableHead className="text-admin-muted-foreground">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {orders.map((order) => (
                <TableRow key={order.id}>
                  <TableCell>
                    <Checkbox
                      checked={selectedOrders.includes(order.id)}
                      onCheckedChange={(checked) => 
                        handleSelectOrder(order.id, checked as boolean)
                      }
                    />
                  </TableCell>
                  <TableCell className="font-mono text-sm text-admin-foreground">
                    #{order.id.slice(0, 8)}
                  </TableCell>
                  <TableCell>
                    <div className="space-y-1">
                      <p className="font-medium text-admin-foreground">
                        {order.profiles.full_name}
                      </p>
                      <p className="text-sm text-admin-muted-foreground">
                        {order.profiles.email}
                      </p>
                    </div>
                  </TableCell>
                  <TableCell className="text-admin-foreground">
                    {order.order_items?.length || 0} items
                  </TableCell>
                  <TableCell className="font-medium text-admin-foreground">
                    ${Number(order.total_amount).toFixed(2)}
                  </TableCell>
                  <TableCell>
                    <OrderStatusBadge status={order.status || ''} />
                  </TableCell>
                  <TableCell>
                    <PaymentStatusBadge status={order.payment_status || ''} />
                  </TableCell>
                  <TableCell className="text-admin-muted-foreground">
                    {order.created_at && formatDistanceToNow(new Date(order.created_at), { addSuffix: true })}
                  </TableCell>
                  <TableCell>
                    <OrderActionButtons
                      orderId={order.id}
                      currentStatus={order.status || ''}
                      onViewOrder={() => onViewOrder(order)}
                      onUpdateStatus={onUpdateStatus}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default OrdersTable;
